import React, { useState, useEffect, useContext } from "react";
import { API_URL } from "../../components/api";
import {
    DataGrid,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarExport,
    GridFooterContainer,
    GridPagination,
} from "@mui/x-data-grid";
import Typography from "@mui/material/Typography";
import ExcelSVG from "./ExcelSVG.svg";
import FilterSVG from "./FilterSVG.svg";
import * as XLSX from "xlsx";
import Checkbox from "@mui/material/Checkbox";
import CustomizedActions from "./BulkActions";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import AuthContext from "../../context/AuthContext";
import axios from "axios";

import Notes from "./notes";
import ReceiptTable from "./ReceiptsTable";
import Notification from "./SendNotification";
import Cards from "./labels";
import Statement from "./statement";
import "./report.css";
import { sortByPaymentDate } from "./paymentDateSort";

function NegativeBalances() {
    const { user } = useContext(AuthContext);

    // --- State Management ---
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [Client, setClient] = useState("");
    const [Fullname, setFullname] = useState("");
    const [Cl, setCl] = useState("");
    const [checkedRows, setCheckedRows] = useState([]);

    // --- Modal Visibility States ---
    const [show, setShow] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [showReceipt, setShowReceipt] = useState(false);
    const [showStatement, setShowStatement] = useState(false);
    const [showPostponeModal, setShowPostponeModal] = useState(false);
    const [showCollectedModal, setShowCollectedModal] = useState(false);
    const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);
    const [showFilterModal, setShowFilterModal] = useState(false);

    const [searchInput, setSearchInput] = useState("");
    const [pageSize, setPageSize] = useState(100);
    const [searchFilterTimeout, setSearchFilterTimeout] = useState(null);

    const [filters, setFilters] = useState({
        status: "",
        fullname_en: "",
        suffix: "",
        village_name: "",
        collector: "",
    });

    // --- Handlers ---
    const CloseCollect = () => setShow(false);
    const ShowCollect = () => setShow(true);
    const CloseNotification = () => setShowNotification(false);
    const ShowNotification = () => setShowNotification(true);
    const CloseReceipt = () => setShowReceipt(false);
    const ShowReceipt = () => setShowReceipt(true);
    const CloseStatement = () => setShowStatement(false);
    const ShowStatement = () => setShowStatement(true);

    const ShowPostponeModal = (sub_id) => {
        setSelectedSubscriptionId(sub_id);
        setShowPostponeModal(true);
    };

    const ShowCollectedModal = (sub_id) => {
        setSelectedSubscriptionId(sub_id);
        setShowCollectedModal(true);
    };

    const fetchData = async () => {
    try {
        setLoading(true);
        const authTokens = JSON.parse(localStorage.getItem("authTokens"));
        const response = await axios.get(`${API_URL}/accounting/info/TOTAL/`, {
            headers: { Authorization: `Bearer ${authTokens.access}` },
            params: { username: user.username },
        });

        if (response.status === 200) {
            const rows = sortByPaymentDate(Array.isArray(response.data) ? response.data : []);
            const negativeData = rows.filter(row => {
                const bal = Number(row.balance || 0);
                return bal < 0;
            });
            setData(negativeData);
        }
    } catch (error) {
        console.error("Fetch Error:", error);
    } finally {
        setLoading(false);
    }
};

    useEffect(() => {
        fetchData();
    }, [user.username]);

    const handlePostponeConfirm = async () => {
        try {
            const authTokens = JSON.parse(localStorage.getItem("authTokens"));
            await axios.post(`${API_URL}/accounting/info/Postpone/`, 
                { action: "postpone", sub_id: selectedSubscriptionId, username: user.username },
                { headers: { Authorization: `Bearer ${authTokens.access}` } }
            );
            fetchData();
            setShowPostponeModal(false);
        } catch (error) { console.error(error); }
    };

    const handleCollectedConfirm = async () => {
        try {
            const authTokens = JSON.parse(localStorage.getItem("authTokens"));
            await axios.post(`${API_URL}/accounting/info/Collected/`, 
                { action: "toBeCollected", sub_id: selectedSubscriptionId, username: user.username },
                { headers: { Authorization: `Bearer ${authTokens.access}` } }
            );
            fetchData();
            setShowCollectedModal(false);
        } catch (error) { console.error(error); }
    };

    const handleSearchChange = (event) => {
        const query = event.target.value;
        setSearchInput(query);
        if (searchFilterTimeout) clearTimeout(searchFilterTimeout);
        setSearchFilterTimeout(setTimeout(() => setSearchQuery(query), 100));
    };

    // --- Filtering Logic for Negative Balances ---
    const filteredRows = data.filter((row) => {
    const searchMatch = searchQuery.split(" ").every((word) =>
        Object.values(row).some((value) => 
            String(value).toLowerCase().includes(word.toLowerCase().trim())
        )
    );

    const modalFilterMatch = Object.keys(filters).every((key) => {
        if (!filters[key]) return true;
        return String(row[key] || "").toLowerCase().includes(String(filters[key]).toLowerCase());
    });

    return searchMatch && modalFilterMatch;
});

    const getStatusCellStyle = (params) => {
        const statusMap = {
            offline: "status-offline",
            online: "status-online",
            inactive: "status-inactive",
            expired: "status-expired",
            hold: "status-hold",
            HSI: "status-hsi"
        };
        return statusMap[params.value] || "";
    };

    const columns = [
        {
            field: "checkbox",
            headerName: "",
            width: 60,
            renderHeader: () => (
                <Checkbox
                    indeterminate={checkedRows.length > 0 && checkedRows.length < filteredRows.length}
                    checked={checkedRows.length === filteredRows.length && filteredRows.length > 0}
                    onChange={() => {
                        if (checkedRows.length === filteredRows.length) setCheckedRows([]);
                        else setCheckedRows(filteredRows.map((row) => row.subscription_id));
                    }}
                />
            ),
            renderCell: (params) => (
                <Checkbox
                    checked={checkedRows.includes(params.row.subscription_id)}
                    onChange={(e) => {
                        const checked = e.target.checked;
                        setCheckedRows(prev => checked ? [...prev, params.row.subscription_id] : prev.filter(id => id !== params.row.subscription_id));
                    }}
                />
            ),
        },
        { field: "status", headerName: "Status", flex: 1, minWidth: 100, cellClassName: (params) => getStatusCellStyle(params) },
        { field: "fullname_en", headerName: "Full Name", flex: 2, minWidth: 200 },
        { 
          field: "balance", 
          headerName: "Credit (USD)", 
          flex: 1, 
          minWidth: 130,
          renderCell: (params) => (
              <span style={{ color: "#2ecc71", fontWeight: "bold" }}>
                  {params.value} $
              </span>
          )
         },
        { field: "village_name", headerName: "Village", flex: 1, minWidth: 120 },
        { field: "collector", headerName: "Collector", flex: 1, minWidth: 150 },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 700,
            flex: 2,
            renderCell: (params) => (
                <div>
                    <button className="edit-client" onClick={() => { setClient(params.row.subscription_id); setFullname(params.row.fullname_en); ShowCollect(); }}>Notes</button>
                    <button className="edit-client" onClick={() => { setCl(params.row.client_id); ShowReceipt(); }}>Receipt</button>
                    <button className="edit-client" onClick={() => { setClient(params.row.subscription_id); setFullname(params.row.fullname_en); ShowNotification(); }}>Notifications</button>
                    <button className="edit-client postpone-btn" onClick={() => ShowPostponeModal(params.row.subscription_id)}>Postpone</button>
                    <button className="edit-client postpone-btn" onClick={() => ShowCollectedModal(params.row.subscription_id)}>Collected</button>
                    <button onClick={() => { ShowStatement(); setCl(params.row.client_id); }} className="edit-client">Statement</button>
                </div>
            ),
        },
    ];

    return (
        <div className="InternetTable">
            <div className="actions">
                <CustomizedActions subscriptions={checkedRows} deselect={() => setCheckedRows([])} fetch={fetchData} />
                <input type="text" placeholder="Search negative balances..." value={searchInput} onChange={handleSearchChange} className="search-client" />
            </div>
            
            {!loading && (
                <div style={{ height: 570, width: "100%" }}>
                    <DataGrid
                        rows={filteredRows}
                        columns={columns}
                        getRowId={(row) => row.subscription_id}
                        components={{
                            Footer: () => (
                                <GridFooterContainer style={{ display: "flex", justifyContent: "space-between" }}>
                                    <Typography style={{ marginLeft: "16px", color: "white" }}>{`Total Negative: ${filteredRows.length}`}</Typography>
                                    <GridPagination />
                                </GridFooterContainer>
                            ),
                            Toolbar: () => (
                                <GridToolbarContainer>
                                    <GridToolbarColumnsButton />
                                    <Button onClick={() => setShowFilterModal(true)} variant="outline-primary" style={{ border: "none" }}><img src={FilterSVG} alt="F" style={{ width: "18px", marginRight: "8px" }} /> Filter</Button>
                                    <GridToolbarExport />
                                    <Button onClick={() => {
                                        const ws = XLSX.utils.json_to_sheet(filteredRows);
                                        const wb = XLSX.utils.book_new();
                                        XLSX.utils.book_append_sheet(wb, ws, "Debt");
                                        XLSX.writeFile(wb, "Debt_Report.xlsx");
                                    }} variant="outline-success" style={{ border: "none" }}><img src={ExcelSVG} alt="E" style={{ width: "20px", marginRight: "8px" }} /> Excel</Button>
                                </GridToolbarContainer>
                            ),
                        }}
                        pageSize={pageSize}
                        onPageSizeChange={(s) => setPageSize(s)}
                        rowsPerPageOptions={[10, 100, 1000]}
                        pagination
                        disableSelectionOnClick
                    />
                </div>
            )}

            {/* Modals from Imad's code adapted for Negative Balances */}
            <Modal show={showPostponeModal} onHide={() => setShowPostponeModal(false)}>
                <Modal.Header closeButton><Modal.Title>Confirm Postpone</Modal.Title></Modal.Header>
                <Modal.Body>Are you sure you want to postpone this debt?</Modal.Body>
                <Modal.Footer><Button variant="primary" onClick={handlePostponeConfirm}>Confirm</Button></Modal.Footer>
            </Modal>

            <Modal show={showCollectedModal} onHide={() => setShowCollectedModal(false)}>
                <Modal.Header closeButton><Modal.Title>Confirm To Be Collected</Modal.Title></Modal.Header>
                <Modal.Body>Mark this subscription as "To Be Collected"?</Modal.Body>
                <Modal.Footer><Button variant="primary" onClick={handleCollectedConfirm}>Confirm</Button></Modal.Footer>
            </Modal>

            <Modal show={show} onHide={CloseCollect} className="modal-lg">
                <div className="forma">
                    <Modal.Header closeButton><Modal.Title>{Fullname} Notes</Modal.Title></Modal.Header>
                    <Notes sub_id={Client} />
                </div>
            </Modal>

            <Modal show={showNotification} onHide={CloseNotification} className="modal-lg">
                <div className="forma">
                    <Modal.Header closeButton><Modal.Title>Send Notification to {Fullname}</Modal.Title></Modal.Header>
                    <Notification sub_id={Client} />
                </div>
            </Modal>

            <Modal show={showReceipt} onHide={CloseReceipt} className="modal-lg">
                <div className="forma">
                    <Modal.Header closeButton><Modal.Title>{Fullname} Receipts</Modal.Title></Modal.Header>
                    <ReceiptTable client_id={Cl} />
                </div>
            </Modal>

            <Modal show={showStatement} onHide={CloseStatement} className="modal-xl">
                <Modal.Header closeButton><Modal.Title>Financial Statement</Modal.Title></Modal.Header>
                <Modal.Body><Statement client_id={Cl} /></Modal.Body>
            </Modal>
        </div>
    );
}

export default NegativeBalances;